import { Switch, Route } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/lib/theme-provider";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { ToolProvider } from "@/lib/tool-context";
import { ProjectProvider } from "@/lib/project-context";
import { ConnectivityProvider } from "@/lib/connectivity-context";
import { queryClient } from "@/lib/queryClient";
import React, { Suspense } from "react";

// Eagerly loaded pages (small / frequently visited)
import Dashboard from "@/pages/dashboard";
import Projects from "@/pages/projects";
import NotFound from "@/pages/not-found";

// Lazy-loaded pages (large / infrequently visited)
const ProjectNew = React.lazy(() => import("@/pages/project-new"));
const ProjectDetail = React.lazy(() => import("@/pages/project-detail"));
const FieldOperations = React.lazy(() => import("@/pages/field-operations"));
const Analysis = React.lazy(() => import("@/pages/analysis"));
const Results = React.lazy(() => import("@/pages/results"));
const DefectReview = React.lazy(() => import("@/pages/defect-review"));
const ManualAnalysis = React.lazy(() => import("@/pages/manual-analysis"));
const RunAnalysis = React.lazy(() => import("@/pages/run-analysis"));
const Reports = React.lazy(() => import("@/pages/reports"));
const Settings = React.lazy(() => import("@/pages/settings"));

function AppLayout() {
  return (
    <SidebarProvider>
      <div className="flex h-screen w-full">
        <AppSidebar />
        <main className="flex-1 overflow-y-auto">
          <Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>}>
          <Switch>
            <Route path="/" component={Dashboard} />
            <Route path="/projects" component={Projects} />
            <Route path="/projects/new" component={ProjectNew} />
            <Route path="/projects/:id" component={ProjectDetail} />
            <Route path="/field" component={FieldOperations} />
            <Route path="/analysis" component={Analysis} />
            <Route path="/results" component={Results} />
            <Route path="/review/:id" component={DefectReview} />
            <Route path="/manual-analysis" component={ManualAnalysis} />
            <Route path="/run-analysis" component={RunAnalysis} />
            <Route path="/reports" component={Reports} />
            <Route path="/settings" component={Settings} />
            <Route component={NotFound} />
          </Switch>
          </Suspense>
        </main>
      </div>
    </SidebarProvider>
  );
}

export default function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="sparrow-theme">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ConnectivityProvider>
            <ToolProvider>
              <ProjectProvider>
                <AppLayout />
                <Toaster />
              </ProjectProvider>
            </ToolProvider>
          </ConnectivityProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
